import styled, { css } from "styled-components";

import {
  OptionEnum,
  OptionModeEnum,
  OptionTypeEnum,
} from "@/components/Option";
import { HEXtoRGBA } from "@/styles/helper";
import { IThemeProps } from "@/styles/Theme";

type ResultProps = IThemeProps<{
  type: OptionTypeEnum;
  mode: OptionModeEnum;
  answer?: OptionEnum;
  percent?: number;
  isSecret?: boolean;
}>;
const Result = styled.div<ResultProps>`
  height: 13px;
  ${(props: ResultProps) => props.type === OptionTypeEnum.poll && PollResult}
  ${(props: ResultProps) => props.type === OptionTypeEnum.quiz && QuizResult}
`;

const WhiteBox = styled.div`
  background-color: ${(props: any) => props.theme.colors.white};
  padding: ${(props: any) => props.theme.borders.answerTile};
`;

const Flexbox = styled.div`
  height: 13px;
  display: flex;
  align-items: center;
  position: relative;
  ${(props: any) => props.type === OptionTypeEnum.poll && PollFlexbox};
  ${(props: any) => props.type === OptionTypeEnum.quiz && QuizFlexbox};
`;

const PollFlexbox = css`
  justify-content: ${(props: any) =>
    props.isSecret ? "center" : "flex-start"};
  background-color: ${(props: any) => {
    const {
      pollOptionResultBarBackground,
      pollOptionResultBarBackgroundModeResult,
      pollOptionResultBarBackgroundModeResultVote,
    } = props.theme.colors;

    if (props.mode === OptionModeEnum.results) {
      if (props.answer === OptionEnum.vote) {
        return pollOptionResultBarBackgroundModeResultVote;
      }
      return pollOptionResultBarBackgroundModeResult;
    }
    return pollOptionResultBarBackground;
  }};
`;

const QuizFlexbox = css`
  justify-content: center;
  background-color: ${(props: any) => {
    const {
      quizOptionResultBarBackground,
      quizOptionResultBarBackgroundCorrect,
      quizOptionResultBarBackgroundWrong,
    } = props.theme.colors;

    if (props.answer) {
      if (props.answer === OptionEnum.correct) {
        return quizOptionResultBarBackgroundCorrect;
      } else {
        return quizOptionResultBarBackgroundWrong;
      }
    }
    return quizOptionResultBarBackground;
  }};
`;

const PollResult = css`
  width: ${(props: ResultProps) => {
    if (props.mode === OptionModeEnum.results) {
      if (props.isSecret) {
        // Full width for all result bars
        return 100;
      }
      return props.percent ? props.percent : 0;
    } else {
      return 0;
    }
  }}%;
  transition: ${(props: ResultProps) => {
    if (
      props.mode === OptionModeEnum.results &&
      !props.answer &&
      props.isSecret
    ) {
      // Not selected answer for a secret poll - no animation
      return "none";
    } else if (props.isSecret) {
      // Selected answer for a secret poll - easeInOutQuart
      return "width 600ms cubic-bezier(0.77, 0, 0.175, 1) 100ms";
    }
    // Normal poll
    return "width 800ms ease-in";
  }};
  background-color: ${(props: ResultProps) => {
    const {
      pollOptionResultBarBackground,
      pollOptionResultBarModeResult,
      pollOptionResultBarModeResultVote,
    } = props.theme.colors;
    if (
      props.mode === OptionModeEnum.results &&
      props.answer === OptionEnum.vote
    ) {
      return pollOptionResultBarModeResultVote;
    } else if (props.isSecret) {
      return pollOptionResultBarBackground;
    }
    return pollOptionResultBarModeResult;
  }};
`;

const QuizResult = css`
  width: ${(props: ResultProps) =>
    props.answer &&
    (props.answer === OptionEnum.correct || props.answer === OptionEnum.wrong)
      ? 100
      : 0}%;
  transition: width 0.8s ease-in;
  background-color: ${(props: ResultProps) => {
    const { success, error } = props.theme.colors;
    return props.answer === OptionEnum.correct ? success : error;
  }};
`;

const Text = styled.span`
  position: absolute;
  top: 0;
  right: 6px;
  font-size: ${(props: any) => props.theme.typography.h100};
  line-height: 13px;
  color: ${(props: any) =>
    props.type === OptionTypeEnum.poll && props.answer === OptionEnum.vote
      ? props.theme.colors.resultBarText
      : HEXtoRGBA(props.theme.colors.resultBarText, 0.6)};
  font-weight: 600;
`;

export { Flexbox, Result, Text, WhiteBox };
